import type { GqFieldFilter, GqFilterValue } from "@/admin/FieldConfig.tsx";
import { ColumnFilterInputType } from "@/admin/graphql.gen/graphql.ts";
import { notNull } from "@warrenio/utils/notNull";
import z from "zod";

/** Converts an empty filter to `null`. */
export function removeEmptyFilter(value: GqFilterValue | null): GqFilterValue | null {
    if (value == null || isEmptyFilter(value)) {
        return null;
    }
    return value;
}

export function isEmptyFilter(value: GqFilterValue): boolean {
    return value.value == null && value.value2 == null && value.values == null;
}

//#region Serialization of filters to concise URL format
export type SerializedQuickFilter =
    // Single value (ColumnFilterInputType.Exact)
    | [field: string, value: string]
    // List of values (ColumnFilterInputType.List)
    | [field: string, values: string[]]
    // Range of values (ColumnFilterInputType.Range)
    | [field: string, value: string | null, value2: string | null];

/** Same as {@link SerializedQuickFilter} */
export const SerializedQuickFilterSchema = z.union([
    z.tuple([z.string(), z.string()]),
    z.tuple([z.string(), z.string().nullable(), z.string().nullable()]),
    z.tuple([z.string(), z.array(z.string())]),
]);

/** Convert a GraphQL filter to a serialized format for URL. */
export function serializeQuickFilter(filter: GqFieldFilter): SerializedQuickFilter | undefined {
    const { field, type, value, value2, values } = filter;
    if (type === ColumnFilterInputType.List) {
        return [field, notNull(values)];
    } else if (type === ColumnFilterInputType.Range) {
        return [field, value ?? null, value2 ?? null];
    } else if (value != null) {
        return [field, value];
    }
    return undefined;
}

/** Convert a URL-serialized filter back to a GraphQL filter. */
export function deserializeQuickFilter(f: SerializedQuickFilter): GqFieldFilter {
    const [field, value, value2] = f;
    if (Array.isArray(value)) {
        return { field, type: ColumnFilterInputType.List, values: value };
    } else if (f.length === 3) {
        return { field, type: ColumnFilterInputType.Range, value: value ?? undefined, value2: value2 ?? undefined };
    } else {
        return { field, type: ColumnFilterInputType.Exact, value: value ?? undefined };
    }
}
